import { TransportDistribution } from "@/types/transport-distribution/transport-distribution.types";
import { Column } from "@/components/ui/data-table/types";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Pencil, Archive, ArchiveRestore } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { formatNumberDynamic } from "@/utils/numberFormatter";

export const useTransportDistributionColumns = (
  t: (key: string) => string,
  onEdit: (distribution: TransportDistribution) => void,
  onArchiveAction: (distribution: TransportDistribution) => void
): Column<TransportDistribution>[] => {
  const { claims } = useAuth();
  const isAssimagraUser = claims?.role === "assimagra";
  
  const baseColumns: Column<TransportDistribution>[] = [
    {
      header: t("transportDistribution.table.vehicleType"),
      accessorKey: "vehicleType",
      sortable: true,
      className: "min-w-[180px]",
    },
    {
      header: t("transportDistribution.table.fe_trans"),
      accessorKey: "fe_trans",
      sortable: true,
      className: "min-w-[120px]",
      cell: (_: string, row: TransportDistribution) => <div>{row.fe_trans ? formatNumberDynamic(row.fe_trans) : "-"}</div>,
    },
    {
      header: t("transportDistribution.table.fh_trans"),
      accessorKey: "fh_trans",
      sortable: true,
      className: "min-w-[120px]",
      cell: (_: string, row: TransportDistribution) => <div>{row.fh_trans ? formatNumberDynamic(row.fh_trans) : "-"}</div>,
    },
  ];

  const actionsColumn: Column<TransportDistribution> = {
    header: t("transportDistribution.table.actions"),
    accessorKey: "id",
    sortable: false,
    className: "w-[100px]",
    cell: (_value: string, row: TransportDistribution) => (
      <div className="flex gap-2 justify-end">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon" onClick={() => onEdit(row)} className="h-8 w-8 hover:bg-gray-100">
                <Pencil className="h-4 w-4 text-gray-500" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top" align="center">
              <p>{t("transportDistribution.actions.edit")}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onArchiveAction(row)}
                className="h-8 w-8 hover:bg-gray-100"
              >
                {!row.isArchived ? (
                  <Archive className="h-4 w-4 text-gray-500" />
                ) : (
                  <ArchiveRestore className="h-4 w-4 text-gray-500" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top" align="center">
              <p>{!row.isArchived ? t("transportDistribution.actions.archive") : t("transportDistribution.actions.restore")}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    ),
  };

  return isAssimagraUser ? baseColumns : [...baseColumns, actionsColumn];
}; 